import * as React from 'react';
import { useCallback } from 'react';
import NewPasswordForm from './NewPasswordForm';
import { useDispatch } from 'react-redux';
import { newPasswordAction } from './api/newPasswordAction';
import { ActionType } from '../../core/redux/actionType';
import LoginLayout from '../login/LoginLayout';
import { useLoading, useError } from '../../core/api/hooks';
import { useMount } from '../../core/hooks/useMount';
import { passwordRequirementMessageAction } from './api/passwordRequirementMessageAction';
import { Str } from '../../localization/translationKeys';
import { useMsgText } from '../../localization/hooks';
import CreateSecurityQuestionForm from './CreateSecurityQuestionForm';
import { createSecurityChallengeAction } from './api/createSecurityChallengeAction';
import { securityQuestionKeysAction } from '../forgotPassword/api/securityQuestionKeysAction';
import { useStickyDiff } from '../../core/hooks/useStickyDiff';
import { useAppState } from '../../core/redux/hooks';


type Props = {
  className?: string;
  postSetup: React.ReactNode;
};


/**
 * AccountSetupPage: a component to which the user is automatically directed after their first login or whenever logging
 * in from a password reset invite link. Here the user will set and confirm their first or new password and, if they
 * haven't already, configure their security question and answer.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
const AccountSetupPage: React.FC<Props> = props => {
  const dispatch = useDispatch();

  useMount(() => { dispatch(passwordRequirementMessageAction); });

  const createPassword = useCallback((passwordHash: string) => dispatch(newPasswordAction(passwordHash)), [dispatch]);

  const createPasswordLoading           = useLoading(ActionType.NewPasswordRequest);
  const isLoadingSecurityQuestionResult = useLoading(ActionType.CreateSecurityChallengeRequest);

  const createPasswordError            = !!useError(ActionType.NewPasswordRequest);
  const errorCreatingSecurityChallenge = !!useError(ActionType.CreateSecurityChallengeRequest);

  const questionId   = useAppState(state => state.auth.currentUser?.securityQuestionId);
  const answer       = useAppState(state => state.auth.currentUser?.securityAnswer);
  const questionKeys = useAppState(state => state.auth.securityQuestionKeys ?? []);

  const pwReqMsg         = useAppState(state => state.auth.passwordRequirementMessage);
  const minPwLength      = useAppState(state => state.auth.currentUser?.passwordRequirements.passwordLength);
  const requireSymbol    = useAppState(state => state.auth.currentUser?.passwordRequirements.requireSymbol);
  const needsNewPassword = useAppState(
    state => !!state.auth.currentUser?.requirePasswordChange || state.auth.currentUser?.passwordDigest === null,
  );

  const createSecurityChallenge = useCallback(
    (questionId: number, answer: string) => dispatch(createSecurityChallengeAction(questionId, answer)),
    [dispatch],
  );

  const hasSecurityChallenge = !!questionId && !!answer;

  const pwFormHeaderMsg       = useMsgText(hasSecurityChallenge ? Str.CreatePassword : Str.SetPassword);
  const questionFormHeaderMsg = useMsgText(Str.SetSecurityQuestion);
  const headerMsg             = needsNewPassword ? pwFormHeaderMsg : questionFormHeaderMsg;

  useStickyDiff(
    (prev, next) => prev?.needsNewPassword
                    && !next.needsNewPassword
                    && !next.hasSecurityChallenge
                    && dispatch(securityQuestionKeysAction),
    {
      needsNewPassword:     needsNewPassword,
      hasSecurityChallenge: hasSecurityChallenge,
    },
  );

  return (
    <LoginLayout
      headerMsg={headerMsg}
      isLoading={createPasswordLoading || isLoadingSecurityQuestionResult}
      className={props.className}
    >
      {
        needsNewPassword && (
          <NewPasswordForm
            minPwLength={minPwLength}
            requireSymbol={requireSymbol}
            isLoading={createPasswordLoading}
            serverSideValidationError={createPasswordError}
            createPassword={createPassword}
            pwRequirements={pwReqMsg}
          />
        )
      }
      {
        !needsNewPassword && !hasSecurityChallenge && (
          <CreateSecurityQuestionForm
            securityQuestionKeys={questionKeys}
            createSecurityChallenge={createSecurityChallenge}
            isLoadingCreationQuestionResponse={isLoadingSecurityQuestionResult}
            errorCreatingSecurityChallenge={errorCreatingSecurityChallenge}
          />
        )
      }
      { // Project Select Modal
        !needsNewPassword && hasSecurityChallenge && props.postSetup
      }
    </LoginLayout>
  );
};


export default AccountSetupPage;
